import { useCallback } from 'react';
import { useHasPermission } from '@Contexts/appContext';
import { SingleSelect, type ToolbarOptions, useTableRefetch } from '@synqo/frontend-component-library';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { EnumFrontendLayoutSetting } from '@Model';
import { BILLING_API } from '@Constants/backendConstants';
import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables';
import { initialInvoiceSorting, invoiceColumns } from '@Constants/TableColumns';
import { Permission } from '@Constants/Permission.g';
import type { InvoiceTableModes } from '@Modules/Billing';
import { IHasTableModes } from '@Hooks/useTableModes';
import { useListApi } from '@Hooks/useListApi';
import { InvoicesRepository } from '@Repositories/Billing';

interface IFrameListInvoiceBodyProps extends IHasTableModes<InvoiceTableModes> {}

export function FrameListAllInvoices({
    tableModeValue,
    setTableMode,
    availableTableModes,
}: IFrameListInvoiceBodyProps) {
    const hasPermission = useHasPermission();
    const { refetchRef, refetchTable } = useTableRefetch();
    useRemoteCacheInvalidation('invoices', refetchTable);

    const module = useListApi(InvoicesRepository, BILLING_API, ['Customer', 'Emails']);

    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            'export',
            <SingleSelect
                key="tableMode"
                name="tableMode"
                items={availableTableModes}
                value={tableModeValue}
                onChange={setTableMode}
            />,
        ],
        [availableTableModes, tableModeValue, setTableMode],
    );

    return (
        <>
            <TableWithStoredSettingsAndLayoutSettings
                layoutSettingsContext={EnumFrontendLayoutSetting.InvoicesTableLayout}
                name="InvoiceList"
                columns={invoiceColumns}
                loader={module.loadPage}
                refetchRef={refetchRef}
                canCreate={hasPermission(Permission.BillingInvoicesCreate)}
                useOnClickNavigation
                fullSize
                initialSorting={initialInvoiceSorting}
                toolbarElements={toolbarElements}
            />
        </>
    );
}
